/**
 * Coverage — the dashboard for what we hold.
 *
 * Three layers, deliberately:
 *   1. Metrics. What does it all cost, what's in force, what needs attention next.
 *   2. A compact, sortable list. One line per row, scannable in a glance.
 *   3. Drill-in. Everything else — policy number, term, coverage detail, notes —
 *      lives inside the row and opens only when asked for.
 *
 * The first version rendered every policy fully expanded, which put five paragraphs of
 * notes on screen at once and made it impossible to look at just the auto line. Detail
 * is not the problem; detail shown unconditionally is. One row open at a time.
 */
import { useMemo, useState } from 'react';
import type { CoverageLine, CoverageState, InsuranceStatusSummary } from '../../shared/types';
import { api, money } from '../../lib/api';
import {
  Card,
  Chip,
  ErrorState,
  Loading,
  PanelHeading,
  Select,
  cx,
  useResource,
  type ChipTone,
} from './ui';

const STATE_META: Record<CoverageState, { label: string; chip: ChipTone; dot: string }> = {
  ok: { label: 'In force', chip: 'ok', dot: 'bg-sage' },
  expiring: { label: 'Renews soon', chip: 'near', dot: 'bg-honey' },
  enrolling: { label: 'Enrollment open', chip: 'near', dot: 'bg-honey' },
  lapsed: { label: 'Not in force', chip: 'over', dot: 'bg-clay' },
  unknown: { label: 'No renewal date', chip: 'near', dot: 'bg-honey' },
  missing: { label: 'None on file', chip: 'neutral', dot: 'bg-ink-faint' },
};

/** Lower sorts first. Anything needing a decision outranks anything that doesn't. */
const ATTENTION: Record<CoverageState, number> = {
  lapsed: 0,
  enrolling: 1,
  expiring: 2,
  unknown: 3,
  ok: 4,
  missing: 5,
};

type SortId = 'attention' | 'deadline' | 'premium' | 'gaps' | 'line';

const SORTS: { id: SortId; label: string }[] = [
  { id: 'attention', label: 'Needs attention first' },
  { id: 'deadline', label: 'Soonest deadline' },
  { id: 'premium', label: 'Most expensive' },
  { id: 'gaps', label: 'Most missing info' },
  { id: 'line', label: 'Line of coverage' },
];

interface Deadline {
  days: number;
  verb: string;
}

/**
 * The next thing that actually needs doing on this line.
 *
 * An employer health plan never "renews" on a date you can act on — its only actionable
 * deadline is the enrollment window. Treating renewal as the only kind of deadline would
 * hide the one date on that policy that matters.
 */
function deadlineOf(line: CoverageLine): Deadline | null {
  const p = line.policy;
  if (!p) return null;
  const options: Deadline[] = [];
  if (p.days_to_enrollment_close !== null && p.days_to_enrollment_close >= 0) {
    options.push({ days: p.days_to_enrollment_close, verb: 'enrollment closes' });
  }
  if (p.days_to_expiry !== null) {
    options.push({ days: p.days_to_expiry, verb: p.days_to_expiry < 0 ? 'expired' : 'renews' });
  }
  if (!options.length) return null;
  return options.sort((a, b) => a.days - b.days)[0];
}

function deadlineLabel(d: Deadline | null): string {
  if (!d) return 'No date on file';
  if (d.verb === 'expired') return `Expired ${Math.abs(d.days)}d ago`;
  if (d.days === 0) return `${d.verb} today`;
  return `${d.verb} in ${d.days}d`;
}

export default function PoliciesPanel() {
  const { data, loading, error, reload } = useResource<InsuranceStatusSummary>(
    () => api.get<InsuranceStatusSummary>('/insurance/status'),
    []
  );
  const [sortBy, setSortBy] = useState<SortId>('attention');
  const [openLine, setOpenLine] = useState<string | null>(null);

  const lines = useMemo(() => {
    const all = [...(data?.lines ?? [])];
    const byLine = (a: CoverageLine, b: CoverageLine) => a.label.localeCompare(b.label);

    switch (sortBy) {
      case 'deadline':
        return all.sort((a, b) => {
          const da = deadlineOf(a);
          const db = deadlineOf(b);
          // No date sorts last — it's unknown, not imminent.
          if (da === null && db === null) return byLine(a, b);
          if (da === null) return 1;
          if (db === null) return -1;
          return da.days - db.days || byLine(a, b);
        });
      case 'premium':
        return all.sort(
          (a, b) =>
            (b.policy?.annual_premium_cents ?? -1) - (a.policy?.annual_premium_cents ?? -1) ||
            byLine(a, b)
        );
      case 'gaps':
        // Only lines we actually hold — an optional line we don't carry isn't a gap.
        return all.sort(
          (a, b) =>
            (b.policy ? b.missing_fields.length : -1) -
              (a.policy ? a.missing_fields.length : -1) || byLine(a, b)
        );
      case 'line':
        return all.sort(byLine);
      default:
        return all.sort((a, b) => ATTENTION[a.state] - ATTENTION[b.state] || byLine(a, b));
    }
  }, [data, sortBy]);

  return (
    <section>
      <PanelHeading
        title="Coverage"
        sub="Every line we carry, what it costs, and what needs doing next."
      />

      {loading && <Loading label="Loading coverage" />}
      {error && !loading && <ErrorState message={error} onRetry={reload} />}

      {data && !loading && !error && (
        <div className="space-y-5">
          <Metrics summary={data} />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-lg font-semibold text-ink">
              {data.lines.length} lines
            </h3>
            <label className="flex items-center gap-2 text-sm text-ink-soft">
              <span className="whitespace-nowrap">Sort by</span>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortId)}
                className="w-auto"
              >
                {SORTS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <ul className="space-y-2">
            {lines.map((line) => (
              <LineRow
                key={line.policy_type}
                line={line}
                open={openLine === line.policy_type}
                onToggle={() =>
                  setOpenLine((cur) => (cur === line.policy_type ? null : line.policy_type))
                }
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- metrics

function Metrics({ summary }: { summary: InsuranceStatusSummary }) {
  const inForce = summary.lines.filter((l) => l.policy && l.state !== 'lapsed' && l.state !== 'missing');

  // Only in-force policies count toward what we're paying. A terminated policy's premium
  // is not a household cost, and counting it would overstate the annual number.
  const priced = inForce.filter((l) => l.policy?.annual_premium_cents != null);
  const total = priced.reduce((sum, l) => sum + (l.policy?.annual_premium_cents ?? 0), 0);
  const unpriced = inForce.length - priced.length;

  const next = summary.lines
    .map((l) => ({ line: l, d: deadlineOf(l) }))
    .filter((x): x is { line: CoverageLine; d: Deadline } => x.d !== null && x.d.days >= 0)
    .sort((a, b) => a.d.days - b.d.days)[0];

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <Stat
        label="Annual premiums"
        value={total > 0 ? money(total) : '—'}
        sub={unpriced > 0 ? `${unpriced} with no premium on file` : `across ${priced.length} lines`}
      />
      <Stat
        label="In force"
        value={`${inForce.length}`}
        sub={`of ${summary.lines.length} lines tracked`}
      />
      <Stat
        label="Not in force"
        value={`${summary.lapsed}`}
        sub={summary.lapsed > 0 ? 'needs coverage' : 'nothing lapsed'}
        tone={summary.lapsed > 0 ? 'over' : 'ok'}
      />
      <Stat
        label="Next deadline"
        value={next ? `${next.d.days}d` : '—'}
        sub={next ? `${next.line.label} · ${next.d.verb}` : 'no dates on file'}
        tone={next && next.d.days <= 60 ? 'near' : undefined}
      />
      <Stat
        label="Info gaps"
        value={`${summary.missing_fields}`}
        sub={summary.missing_fields > 0 ? 'fields needed for a review' : 'nothing missing'}
        tone={summary.missing_fields > 0 ? 'near' : 'ok'}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'ok' | 'near' | 'over';
}) {
  const accent =
    tone === 'over' ? 'text-clay' : tone === 'near' ? 'text-honey' : 'text-ink';
  return (
    <Card className="flex flex-col justify-between gap-1 p-3 sm:p-4">
      <span className="text-xs font-medium tracking-wide text-ink-soft uppercase">{label}</span>
      <span className={cx('font-display text-2xl leading-none font-semibold tabular-nums', accent)}>
        {value}
      </span>
      <span className="text-xs leading-snug text-ink-soft">{sub}</span>
    </Card>
  );
}

// ---------------------------------------------------------------- rows

function LineRow({
  line,
  open,
  onToggle,
}: {
  line: CoverageLine;
  open: boolean;
  onToggle: () => void;
}) {
  const meta = STATE_META[line.state];
  const p = line.policy;
  const deadline = deadlineOf(line);
  const panelId = `coverage-${line.policy_type}`;

  return (
    <li className="overflow-hidden rounded-card border border-border bg-surface">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className={cx(
          'flex w-full min-h-14 items-center gap-3 p-3 text-left transition-colors sm:p-4',
          'hover:bg-surface-sunk focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-inset focus-visible:outline-none',
          line.state === 'lapsed' && 'bg-clay-soft hover:bg-clay-soft/80'
        )}
      >
        {/* Status is never colour alone — the dot is decorative, the chip carries the word. */}
        <span className={cx('h-2.5 w-2.5 shrink-0 rounded-full', meta.dot)} aria-hidden="true" />

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-display text-base font-semibold text-ink">{line.label}</span>
            <span className="truncate text-sm text-ink-soft">{p ? p.carrier : 'No policy'}</span>
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <Chip tone={meta.chip}>{meta.label}</Chip>
            <span className="text-xs text-ink-soft">{deadlineLabel(deadline)}</span>
            {p && line.missing_fields.length > 0 && (
              <Chip tone="neutral">{line.missing_fields.length} missing</Chip>
            )}
          </span>
        </span>

        <span className="shrink-0 text-right">
          <span className="block text-sm font-semibold text-ink tabular-nums">
            {p?.annual_premium_cents != null ? money(p.annual_premium_cents) : '—'}
          </span>
          <span className="block text-[11px] text-ink-soft">
            {p?.annual_premium_cents != null ? 'a year' : 'no premium'}
          </span>
        </span>

        <svg
          viewBox="0 0 24 24"
          className={cx(
            'h-4 w-4 shrink-0 text-ink-faint transition-transform',
            open && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div id={panelId} className="border-t border-border p-3 sm:p-4">
          {p ? <PolicyDetail line={line} /> : <NoPolicy line={line} />}
        </div>
      )}
    </li>
  );
}

function PolicyDetail({ line }: { line: CoverageLine }) {
  const p = line.policy!;
  const summary = p.coverage_summary;

  // The review checklist drives the order, so a field we DON'T hold still gets a row and
  // reads "Not on file". Rendering only what exists made a half-documented policy look
  // complete — which is how the auto deductibles sat missing without anyone noticing.
  const expected = line.expected_fields;
  const extras = Object.keys(summary).filter((k) => !expected.includes(k));
  const onFile = expected.length - line.missing_fields.length;

  return (
    <div className="space-y-4">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {p.policy_number && <Row label="Policy number">{p.policy_number}</Row>}
        <Row label="Term">
          {p.effective_date && p.expiration_date
            ? `${p.effective_date} → ${p.expiration_date}`
            : (p.expiration_date ?? <NotOnFile />)}
        </Row>
        {p.enrollment_closes && (
          <Row label="Enrollment">
            {p.enrollment_opens
              ? `${p.enrollment_opens} → ${p.enrollment_closes}`
              : `closes ${p.enrollment_closes}`}
          </Row>
        )}
        {p.agency && <Row label="Agency">{p.agency}</Row>}
      </dl>

      {expected.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium tracking-wide text-ink-soft uppercase">
            Review checklist — {onFile} of {expected.length} on file
          </p>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {expected.map((field) => (
              <Row key={field} label={field}>
                {summary[field]?.trim() ? summary[field] : <NotOnFile />}
              </Row>
            ))}
          </dl>
        </div>
      )}

      {extras.length > 0 && (
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {extras.map((k) => (
            <Row key={k} label={k}>
              {summary[k]}
            </Row>
          ))}
        </dl>
      )}

      {p.notes && (
        <div className="rounded-lg border border-border bg-surface-sunk p-3">
          <p className="text-xs font-medium tracking-wide text-ink-soft uppercase">Notes</p>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-ink-soft">{p.notes}</p>
        </div>
      )}
    </div>
  );
}

/** A field the review needs and we do not hold. Said plainly, never left blank. */
function NotOnFile() {
  return <span className="text-clay italic">Not on file</span>;
}

function NoPolicy({ line }: { line: CoverageLine }) {
  return (
    <p className="max-w-prose text-sm leading-relaxed text-ink-soft">
      {line.required
        ? 'Nothing on file for a coverage this household should carry.'
        : 'Not carried, or not entered yet.'}
    </p>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-ink-soft">{label}</dt>
      <dd className="min-w-0 font-medium text-ink">{children}</dd>
    </div>
  );
}
